import {
  PHYS_SCALE,
  FIXED_TIMESTEP,
  POSITION_ITERATIONS,
  VELOCITY_ITERATIONS,
  b2AABB,
  b2Body,
  b2BodyDef,
  b2CircleShape,
  b2DistanceJointDef,
  b2FixtureDef,
  b2PolygonShape,
  b2PrismaticJointDef,
  b2RevoluteJointDef,
  b2Vec2,
  b2World,
} from "./box2d-compat.js";
import { PlayerBox } from "./player-box.js";
import { MyContactListener } from "./contact-listener.js";
import { displayBounds } from "./flash-display-bounds.js";

const LEFT_KEYS = ["ArrowLeft", "KeyA"];
const RIGHT_KEYS = ["ArrowRight", "KeyD"];
const UP_KEYS = ["ArrowUp", "KeyW", "Space"];
const CAMERA_EASE = 1 - Math.pow(2, -10 / 31);
const CHECKPOINT_FRAME_COUNT = 5;
const LEVEL_AIM_FRAME_COUNT = 20;
const DEATH_DELAY_FRAMES = Math.round(1200 / (1000 / 31));
const STOCK_WIN_DELAY_FRAMES = 88;
const FIXED_WIN_DELAY_FRAMES = 89;
const HAZARD_NAME_PATTERN = /^(ship\d+|shipik\d+|kill\d+)$/;

function sortedFramePaths(folder, frameCount) {
  return Array.from({ length: frameCount }, (_, index) => `${folder}/${index + 1}.png`);
}

function rectsOverlap(a, b) {
  return a.x < b.x + b.width && a.x + a.width > b.x && a.y < b.y + b.height && a.y + a.height > b.y;
}

function playerFlagBounds(player) {
  const size = player.radius * 2;
  const halfExtent = size / 2 * (Math.abs(Math.cos(player.body.GetAngle())) + Math.abs(Math.sin(player.body.GetAngle())));
  return {
    x: player.x - halfExtent,
    y: player.y - halfExtent,
    width: halfExtent * 2,
    height: halfExtent * 2,
  };
}

function drawStageImage(ctx, image, levelData) {
  const { layout, pngLayout } = levelData;
  if ((levelData.imagePath ?? "").endsWith(".png") && pngLayout) {
    ctx.drawImage(
      image,
      pngLayout.cropX,
      pngLayout.cropY,
      pngLayout.cropWidth,
      pngLayout.cropHeight,
      layout.imageOffsetX,
      layout.imageOffsetY,
      layout.imageWidth,
      layout.imageHeight,
    );
    return;
  }
  ctx.drawImage(image, layout.imageOffsetX, layout.imageOffsetY);
}

function drawAnimatedFrame(ctx, image, symbolData, x, y, rotation = 0) {
  if (!image?.complete || !symbolData?.layout) {
    return;
  }
  ctx.save();
  ctx.translate(x, y);
  ctx.rotate(rotation);
  ctx.drawImage(
    image,
    symbolData.layout.imageOffsetX,
    symbolData.layout.imageOffsetY,
    symbolData.layout.imageWidth,
    symbolData.layout.imageHeight,
  );
  ctx.restore();
}

function getBodyPositionInPixels(body) {
  const position = body.GetPosition();
  return {
    x: position.x * PHYS_SCALE,
    y: position.y * PHYS_SCALE,
  };
}

function createJointBag() {
  return {
    prismatic: Object.create(null),
    revolute: Object.create(null),
    distance: Object.create(null),
  };
}

const LEVEL_CONFIG = {
  4: { deathY: 650, dynamicSprites: [], hazards: [] },
  5: { deathY: 650, dynamicSprites: [], hazards: [] },
  6: { deathY: 1060, dynamicSprites: [], hazards: [] },
  7: { deathY: 500, dynamicSprites: [], hazards: [] },
  8: { deathY: 1800, dynamicSprites: [], hazards: [] },
  9: { deathY: 530, dynamicSprites: [], hazards: [] },
  10: { deathY: 500, dynamicSprites: [], hazards: [] },
  11: { deathY: 500, dynamicSprites: [], hazards: [] },
  12: { deathY: 500, dynamicSprites: [], hazards: [] },
  13: { deathY: 500, dynamicSprites: [], hazards: [] },
  14: { deathY: 750, dynamicSprites: [], hazards: [] },
  15: { deathY: 860, dynamicSprites: [], hazards: [] },
  16: { deathY: 1000, dynamicSprites: [], hazards: [] },
  17: { deathY: 960, dynamicSprites: [], hazards: [] },
};

const LEVEL_SUPPLEMENTAL_BODIES = {};

export class LevelRuntime {
  static levelFlags = {
    7: { redCheckLevel: false },
    13: { greenCheckLevel: false },
    16: { isStrelka: false },
  };

  constructor(levelData, clockOrOptions = null, maybeOptions = null) {
    const hasClock = clockOrOptions && typeof clockOrOptions.update === "function";
    const options = hasClock ? (maybeOptions ?? {}) : (clockOrOptions ?? {});
    const { audioEnabled = true, assetsEnabled = true, fixedLevelTransitionTiming = false } = options;
    this.levelData = levelData;
    this.levelId = levelData.id;
    this.clock = hasClock ? clockOrOptions : null;
    this.config = LEVEL_CONFIG[this.levelId] ?? { deathY: 1200, dynamicSprites: [], hazards: [] };
    this.world = new b2World(new b2Vec2(0, 10), true);
    this.contactListener = new MyContactListener();
    this.world.SetContactListener(this.contactListener);

    this.complete = false;
    this.failed = false;
    this.winActive = false;
    this.winCounter = -1;
    this.deathCounter = -1;
    this.camera = { x: 0, y: 0 };
    this.spikesKill = false;
    this.staticBodies = [];
    this.dynamicBodies = {};
    this.namedBodies = {};
    this.joints = [];
    this.physicsJoints = createJointBag();
    this.particles = [];
    this.assetsEnabled = assetsEnabled;
    this.audioEnabled = audioEnabled;
    this.fixedLevelTransitionTiming = fixedLevelTransitionTiming === true;
    this.winDelayFrames = this.fixedLevelTransitionTiming ? FIXED_WIN_DELAY_FRAMES : STOCK_WIN_DELAY_FRAMES;

    this.stageImage = this.assetsEnabled ? new Image() : null;
    if (this.stageImage) {
      this.stageImage.src = levelData.foregroundSvgPath ?? levelData.svgPath;
    }

    this.goalFrames = this.assetsEnabled ? sortedFramePaths("scratch/l1_png/DefineSprite_68", LEVEL_AIM_FRAME_COUNT).map((src) => {
      const image = new Image();
      image.src = src;
      return image;
    }) : [];
    this.goalCurrentFrame = 1;
    this.goalPlaying = false;

    this.checkpointFrames = this.assetsEnabled ? sortedFramePaths("scratch/l1_png/DefineSprite_85", CHECKPOINT_FRAME_COUNT).map((src) => {
      const image = new Image();
      image.src = src;
      return image;
    }) : [];

    this.namedRects = {};

    this.refreshNamedRects();

    this.checkpoints = Object.entries(this.namedRects)
      .filter(([name]) => /^checkPoint\d+$/.test(name))
      .sort((a, b) => Number(a[0].replace("checkPoint", "")) - Number(b[0].replace("checkPoint", "")))
      .map(([name, rect], index) => ({
        name,
        index,
        rect,
        currentFrame: index === 0 ? 1 : 1,
        playing: false,
      }));
    this.checkpointIndex = 0;

    const goalLayout = this.levelData.layout.namedChildren.levelAim ?? { x: 0, y: 0, width: 20, height: 20 };
    this.goalRect = this.namedRects.levelAim ?? displayBounds(goalLayout, this.levelData.symbols?.levelAim, "levelAim");
    this.goalPosition = goalLayout;
    this.goalSymbol = this.levelData.symbols?.levelAim ?? null;
    this.hazardRects = Object.entries(this.namedRects)
      .filter(([name]) => HAZARD_NAME_PATTERN.test(name))
      .map(([name, rect]) => ({
        name,
        rect,
      }));

    this.image = this.stageImage;
    this.levelState = {};

    this.createBodies();
    this.configureLevel();
    this.applyPersistentFlags();
    this.resetPlayer();
  }

  setSpikesKill(enabled) {
    this.spikesKill = enabled;
  }

  setFixedLevelTransitionTiming(enabled) {
    this.fixedLevelTransitionTiming = enabled === true;
    this.winDelayFrames = this.fixedLevelTransitionTiming ? FIXED_WIN_DELAY_FRAMES : STOCK_WIN_DELAY_FRAMES;
  }

  serializeState() {
    return {
      checkpointIndex: this.checkpointIndex,
      levelFlags: LevelRuntime.levelFlags,
    };
  }

  getBody(name) {
    return this.namedBodies[name] ?? this.dynamicBodies[name] ?? null;
  }

  requireBody(name, context = null) {
    const body = this.getBody(name);
    if (!body) {
      throw new Error(`Missing body '${name}'${context ? ` in ${context}` : ""}`);
    }
    return body;
  }

  createBodies() {
    const bodyEntries = [...this.levelData.bodies, ...(LEVEL_SUPPLEMENTAL_BODIES[this.levelId] ?? [])];
    for (const entry of bodyEntries) {
      const layout = this.levelData.layout.namedChildren[entry.name];
      if (!layout) {
        continue;
      }
      const bodyDef = new b2BodyDef();
      bodyDef.type = entry.density > 0 ? b2Body.b2_dynamicBody : b2Body.b2_staticBody;
      bodyDef.position.Set(layout.x / PHYS_SCALE, layout.y / PHYS_SCALE);
      bodyDef.angle = ((layout.rotation ?? 0) * Math.PI) / 180;
      bodyDef.userData = { __kind: "body", name: entry.name };
      const body = this.world.CreateBody(bodyDef);
      this.namedBodies[entry.name] = body;
      if (entry.shape === "Circle") {
        const fixtureDef = new b2FixtureDef();
        fixtureDef.density = entry.density;
        fixtureDef.friction = entry.friction;
        fixtureDef.restitution = entry.restitution;
        fixtureDef.shape = new b2CircleShape((entry.data[0] / 2) / PHYS_SCALE);
        fixtureDef.shape.SetLocalPosition(new b2Vec2((entry.data[0] / 2) / PHYS_SCALE, (entry.data[0] / 2) / PHYS_SCALE));
        body.CreateFixture(fixtureDef);
      } else {
        for (const polygon of entry.data) {
          const fixtureDef = new b2FixtureDef();
          fixtureDef.density = entry.density;
          fixtureDef.friction = entry.friction;
          fixtureDef.restitution = entry.restitution;
          fixtureDef.shape = new b2PolygonShape();
          const vertices = polygon.map(([x, y]) => new b2Vec2(x / PHYS_SCALE, y / PHYS_SCALE));
          fixtureDef.shape.SetAsArray(vertices, vertices.length);
          body.CreateFixture(fixtureDef);
        }
      }
      this.dynamicBodies[entry.name] = body;
      if (entry.density > 0) {
        body.SetAwake(true);
      } else {
        this.staticBodies.push(body);
      }
    }
  }

  getDisplayLayout(name) {
    const layout = this.levelData.layout.namedChildren[name];
    if (!layout) {
      return null;
    }
    const body = this.getBody(name);
    if (!body) {
      return layout;
    }
    const position = body.GetPosition();
    return {
      ...layout,
      x: position.x * PHYS_SCALE,
      y: position.y * PHYS_SCALE,
      rotation: body.GetAngle() * (180 / Math.PI),
    };
  }

  refreshNamedRects() {
    this.namedRects = Object.fromEntries(
      Object.keys(this.levelData.layout.namedChildren).map((name) => {
        const layout = this.getDisplayLayout(name);
        return [name, displayBounds(layout, this.levelData.symbols?.[name], name) ?? layout];
      }),
    );
  }

  resetPlayer() {
    if (this.player?.alive) {
      this.player.kill(this.world);
    }
    const checkpoint = this.checkpoints[this.checkpointIndex] ?? this.checkpoints[0] ?? { rect: { x: 16.9, y: 249.3 } };
    this.player = new PlayerBox(this.world, checkpoint.rect.x, checkpoint.rect.y);
    this.player.body.SetUserData({ __kind: "player", ref: this.player });
    this.camera.x = -checkpoint.rect.x + 275;
    this.camera.y = -checkpoint.rect.y + 200;
    this.particles = [];
    this.deathCounter = -1;
    this.failed = false;
    this.winActive = false;
    this.complete = false;
    this.goalCurrentFrame = 1;
    this.goalPlaying = false;
    for (const checkpointState of this.checkpoints) {
      checkpointState.currentFrame = checkpointState.index < this.checkpointIndex ? CHECKPOINT_FRAME_COUNT : 1;
      checkpointState.playing = false;
    }
  }

  getBodyAtPoint(x, y, includeStatic = false) {
    const point = new b2Vec2(x, y);
    const aabb = new b2AABB();
    aabb.lowerBound.Set(x - 0.001, y - 0.001);
    aabb.upperBound.Set(x + 0.001, y + 0.001);
    let foundBody = null;
    this.world.QueryAABB((fixture) => {
      const body = fixture.GetBody();
      if ((body.GetType() !== b2Body.b2_staticBody || includeStatic) && fixture.GetShape().TestPoint(body.GetTransform(), point)) {
        foundBody = body;
        return false;
      }
      return true;
    }, aabb);
    return foundBody;
  }

  getPlayerRect() {
    return playerFlagBounds(this.player);
  }

  playerOverlapsRect(rect) {
    return Boolean(rect && this.player?.isLive?.() && rectsOverlap(this.getPlayerRect(), rect));
  }

  bodyIsInPlayerContacts(body) {
    return this.contactListener.playerContactBodies.indexOf(body) !== -1;
  }

  getNamedRect(name) {
    return this.namedRects[name] ?? null;
  }

  bodyPixels(name) {
    const body = this.getBody(name);
    return body ? getBodyPositionInPixels(body) : this.getNamedRect(name);
  }

  hitNamedObject(name) {
    const rect = this.getNamedRect(name);
    if (!rect || !this.player?.isLive()) {
      return false;
    }
    return this.playerOverlapsRect(rect);
  }

  playCheckpoint(index) {
    const checkpointState = this.checkpoints[index];
    if (!checkpointState) {
      return;
    }
    if (index > this.checkpointIndex) {
      this.checkpointIndex = index;
    }
    checkpointState.playing = true;
  }

  destroyBody(body) {
    if (!body) {
      return;
    }
    const userData = body.GetUserData();
    const name = userData?.name;
    this.world.DestroyBody(body);
    if (name) {
      if (this.dynamicBodies[name] === body) {
        delete this.dynamicBodies[name];
      }
      if (this.namedBodies[name] === body) {
        delete this.namedBodies[name];
      }
    }
  }

  recreateBody(name, densityMultiplier = 1) {
    const entry = this.levelData.bodies.find((candidate) => candidate.name === name);
    const layout = this.levelData.layout.namedChildren[name];
    if (!entry || !layout) {
      return null;
    }
    const bodyDef = new b2BodyDef();
    bodyDef.type = b2Body.b2_dynamicBody;
    bodyDef.position.Set(layout.x / PHYS_SCALE, layout.y / PHYS_SCALE);
    bodyDef.angle = ((layout.rotation ?? 0) * Math.PI) / 180;
    bodyDef.userData = { __kind: "body", name };
    const body = this.world.CreateBody(bodyDef);
    if (entry.shape === "Circle") {
      const fixtureDef = new b2FixtureDef();
      fixtureDef.density = entry.density * densityMultiplier;
      fixtureDef.friction = entry.friction;
      fixtureDef.restitution = entry.restitution;
      fixtureDef.shape = new b2CircleShape((entry.data[0] / 2) / PHYS_SCALE);
      fixtureDef.shape.SetLocalPosition(new b2Vec2((entry.data[0] / 2) / PHYS_SCALE, (entry.data[0] / 2) / PHYS_SCALE));
      body.CreateFixture(fixtureDef);
    } else {
      for (const polygon of entry.data) {
        const fixtureDef = new b2FixtureDef();
        fixtureDef.density = entry.density * densityMultiplier;
        fixtureDef.friction = entry.friction;
        fixtureDef.restitution = entry.restitution;
        fixtureDef.shape = new b2PolygonShape();
        const vertices = polygon.map(([x, y]) => new b2Vec2(x / PHYS_SCALE, y / PHYS_SCALE));
        fixtureDef.shape.SetAsArray(vertices, vertices.length);
        body.CreateFixture(fixtureDef);
      }
    }
    body.SetAwake(true);
    this.dynamicBodies[name] = body;
    this.namedBodies[name] = body;
    return body;
  }

  addPrismaticJoint(name, bodyA, bodyB, anchor, axis, options = {}) {
    const jointDef = new b2PrismaticJointDef();
    jointDef.Initialize(bodyA, bodyB, anchor, axis);
    if (options.lowerTranslation != null) {
      jointDef.lowerTranslation = options.lowerTranslation;
    }
    if (options.upperTranslation != null) {
      jointDef.upperTranslation = options.upperTranslation;
    }
    if (options.maxMotorForce != null) {
      jointDef.maxMotorForce = options.maxMotorForce;
    }
    if (options.motorSpeed != null) {
      jointDef.motorSpeed = options.motorSpeed;
    }
    jointDef.enableLimit = Boolean(options.enableLimit);
    jointDef.enableMotor = Boolean(options.enableMotor);
    const joint = this.world.CreateJoint(jointDef);
    this.joints.push(joint);
    this.physicsJoints.prismatic[name] = joint;
    return joint;
  }

  addRevoluteJoint(name, bodyA, bodyB, anchor, options = {}) {
    const jointDef = new b2RevoluteJointDef();
    jointDef.Initialize(bodyA, bodyB, anchor);
    if (options.motorSpeed != null) {
      jointDef.motorSpeed = options.motorSpeed;
    }
    if (options.maxMotorTorque != null) {
      jointDef.maxMotorTorque = options.maxMotorTorque;
    }
    if (options.enableMotor != null) {
      jointDef.enableMotor = options.enableMotor;
    }
    const joint = this.world.CreateJoint(jointDef);
    this.joints.push(joint);
    this.physicsJoints.revolute[name] = joint;
    return joint;
  }

  addDistanceJoint(name, bodyA, bodyB, anchorA, anchorB) {
    const jointDef = new b2DistanceJointDef();
    jointDef.Initialize(bodyA, bodyB, anchorA, anchorB);
    const joint = this.world.CreateJoint(jointDef);
    this.joints.push(joint);
    this.physicsJoints.distance[name] = joint;
    return joint;
  }

  applyPersistentFlags() {
    if (this.levelId === 7 && LevelRuntime.levelFlags[7].redCheckLevel) {
      this.destroyBody(this.namedBodies.redWall);
    }
    if (this.levelId === 13 && LevelRuntime.levelFlags[13].greenCheckLevel) {
      this.destroyBody(this.namedBodies.greenBarier);
    }
  }

  configureLevel() {
    const ground = this.world.GetGroundBody();
    switch (this.levelId) {
      case 4:
        this.levelState.killBoom1Up = false;
        this.levelState.killBoom2Up = false;
        this.levelState.killBoom3Up = false;
        this.addPrismaticJoint("killBoom1", this.dynamicBodies.killBoom1, ground, this.dynamicBodies.killBoom1.GetWorldCenter(), new b2Vec2(0, 1));
        this.addPrismaticJoint("killBoom2", this.dynamicBodies.killBoom2, ground, this.dynamicBodies.killBoom2.GetWorldCenter(), new b2Vec2(0, 1));
        this.addPrismaticJoint("killBoom3", this.dynamicBodies.killBoom3, ground, this.dynamicBodies.killBoom3.GetWorldCenter(), new b2Vec2(0, 1));
        this.addRevoluteJoint("axe1", this.dynamicBodies.axe1, ground, new b2Vec2(785 / PHYS_SCALE, 127 / PHYS_SCALE));
        this.addRevoluteJoint("axe2", this.dynamicBodies.axe2, ground, new b2Vec2(1077 / PHYS_SCALE, 127 / PHYS_SCALE));
        break;
      case 6:
        this.addRevoluteJoint("spin", this.dynamicBodies.spin, ground, this.dynamicBodies.spin.GetPosition(), {
          motorSpeed: -0.3 * Math.PI,
          maxMotorTorque: 5000,
          enableMotor: true,
        });
        ["backBall1", "backBall2", "backBall3"].forEach((name) => {
          this.addRevoluteJoint(name, this.dynamicBodies[name], ground, this.dynamicBodies[name].GetPosition(), {
            motorSpeed: 0.2 * Math.PI,
            maxMotorTorque: 5000,
            enableMotor: true,
          });
        });
        break;
      case 7:
        this.levelState.movePlatform1BodyDirection = 1;
        this.levelState.movePlatform2BodyDirection = 1;
        this.addDistanceJoint("bigMovePlatformLeft", this.dynamicBodies.bigMovePlatform, ground, new b2Vec2(3077 / PHYS_SCALE, -190 / PHYS_SCALE), new b2Vec2(3005 / PHYS_SCALE, -305 / PHYS_SCALE));
        this.addDistanceJoint("bigMovePlatformRight", this.dynamicBodies.bigMovePlatform, ground, new b2Vec2(2944 / PHYS_SCALE, -190 / PHYS_SCALE), new b2Vec2(2873 / PHYS_SCALE, -305 / PHYS_SCALE));
        this.addDistanceJoint("star", this.dynamicBodies.star, ground, new b2Vec2(619 / PHYS_SCALE, -51.5 / PHYS_SCALE), new b2Vec2(552 / PHYS_SCALE, -111 / PHYS_SCALE));
        this.dynamicBodies.star.SetAngularVelocity(-4);
        this.addPrismaticJoint("jumpPlatform1", this.dynamicBodies.jumpPlatform1, ground, this.dynamicBodies.jumpPlatform1.GetWorldCenter(), new b2Vec2(0, 1), {
          lowerTranslation: 0,
          upperTranslation: 1,
          enableLimit: true,
          maxMotorForce: 0,
          motorSpeed: 200,
          enableMotor: true,
        });
        this.addPrismaticJoint("jumpPlatform2", this.dynamicBodies.jumpPlatform2, ground, this.dynamicBodies.jumpPlatform2.GetWorldCenter(), new b2Vec2(0, 1), {
          lowerTranslation: 0,
          upperTranslation: 1,
          enableLimit: true,
          maxMotorForce: 0,
          motorSpeed: 200,
          enableMotor: true,
        });
        this.addPrismaticJoint("movePlatform1", this.dynamicBodies.movePlatform1, ground, this.dynamicBodies.movePlatform1.GetWorldCenter(), new b2Vec2(0, 1));
        this.addPrismaticJoint("movePlatform2", this.dynamicBodies.movePlatform2, ground, this.dynamicBodies.movePlatform2.GetWorldCenter(), new b2Vec2(1, 0));
        break;
      case 8: {
        this.levelState.killer1Up = false;
        this.levelState.killer2Up = false;
        this.levelState.killer3Up = false;
        this.addPrismaticJoint("killer1", this.dynamicBodies.killer1, ground, this.dynamicBodies.killer1.GetWorldCenter(), new b2Vec2(0, 1));
        this.addPrismaticJoint("killer2", this.dynamicBodies.killer2, ground, this.dynamicBodies.killer2.GetWorldCenter(), new b2Vec2(0, 1));
        this.addPrismaticJoint("killer3", this.dynamicBodies.killer3, ground, this.dynamicBodies.killer3.GetWorldCenter(), new b2Vec2(0, 1));
        const wheel1 = this.dynamicBodies.koleco1;
        const wheel2 = this.dynamicBodies.koleco2;
        this.addRevoluteJoint("carWheel1", this.dynamicBodies.car, wheel1, new b2Vec2((this.getNamedRect("koleco1").x + 11.65) / PHYS_SCALE, (this.getNamedRect("koleco1").y + 11.65) / PHYS_SCALE));
        this.addRevoluteJoint("carWheel2", this.dynamicBodies.car, wheel2, new b2Vec2((this.getNamedRect("koleco2").x + 11.65) / PHYS_SCALE, (this.getNamedRect("koleco2").y + 11.65) / PHYS_SCALE));
        break;
      }
      case 9: {
        this.levelState.greenPlatformBodyDirection = 1;
        this.levelState.killRollBallDirection = 1;
        this.addPrismaticJoint("greenPlatform", this.dynamicBodies.greenPlatform, ground, this.dynamicBodies.greenPlatform.GetWorldCenter(), new b2Vec2(1, 0));
        const bridge = [
          "bridgeElement1", "bridgeElement2", "bridgeElement3", "bridgeElement4", "bridgeElement5",
          "bridgeElement6", "bridgeElement7", "bridgeElement8", "bridgeElement9", "bridgeElement10",
        ];
        this.addRevoluteJoint("bridgeStart", this.dynamicBodies.lowPlatform, this.dynamicBodies[bridge[0]], this.dynamicBodies[bridge[0]].GetPosition());
        for (let index = 0; index < bridge.length - 1; index += 1) {
          this.addRevoluteJoint(`bridge${index}`, this.dynamicBodies[bridge[index]], this.dynamicBodies[bridge[index + 1]], this.dynamicBodies[bridge[index + 1]].GetPosition());
        }
        this.addRevoluteJoint("bridgeEnd", this.dynamicBodies.bridgeElement10, this.dynamicBodies.low2Platform, new b2Vec2(this.dynamicBodies.bridgeElement10.GetPosition().x + 50 / PHYS_SCALE, this.dynamicBodies.bridgeElement10.GetPosition().y));
        this.addRevoluteJoint("firstCrank", this.dynamicBodies.firstCrank, ground, this.dynamicBodies.firstCrank.GetPosition(), {
          motorSpeed: 1.8 * Math.PI,
          maxMotorTorque: 5000,
          enableMotor: true,
        });
        this.addRevoluteJoint("secondCrank", this.dynamicBodies.secondCrank, this.dynamicBodies.firstCrank, this.dynamicBodies.secondCrank.GetPosition());
        this.addRevoluteJoint("boomCrank", this.dynamicBodies.boomCrank, this.dynamicBodies.secondCrank, this.dynamicBodies.boomCrank.GetPosition());
        this.addPrismaticJoint("boomCrankSlider", this.dynamicBodies.boomCrank, ground, this.dynamicBodies.boomCrank.GetWorldCenter(), new b2Vec2(1, 0));
        this.addDistanceJoint("jumpBall1", this.dynamicBodies.jumpBall1, ground, new b2Vec2(2236 / PHYS_SCALE, 63 / PHYS_SCALE), new b2Vec2(2172 / PHYS_SCALE, -56 / PHYS_SCALE));
        this.addDistanceJoint("jumpBall2", this.dynamicBodies.jumpBall2, ground, new b2Vec2(2370 / PHYS_SCALE, 63 / PHYS_SCALE), new b2Vec2(2435 / PHYS_SCALE, -56 / PHYS_SCALE));
        this.addDistanceJoint("jumpBall3", this.dynamicBodies.jumpBall3, ground, new b2Vec2(2761 / PHYS_SCALE, 63 / PHYS_SCALE), new b2Vec2(2698 / PHYS_SCALE, -56 / PHYS_SCALE));
        break;
      }
      case 10:
        ["jumpPlatform1", "jumpPlatform2", "jumpPlatform3"].forEach((name) => {
          this.addPrismaticJoint(name, this.dynamicBodies[name], ground, this.dynamicBodies[name].GetWorldCenter(), new b2Vec2(0, 1), {
            lowerTranslation: 0,
            upperTranslation: 1,
            enableLimit: true,
            maxMotorForce: 0,
            motorSpeed: 200,
            enableMotor: true,
          });
        });
        this.addRevoluteJoint("cube1", this.dynamicBodies.cube1, ground, this.dynamicBodies.cube1.GetPosition(), {
          motorSpeed: 0.3 * Math.PI,
          maxMotorTorque: 5000,
          enableMotor: true,
        });
        this.addRevoluteJoint("cube2", this.dynamicBodies.cube2, ground, this.dynamicBodies.cube2.GetPosition(), {
          motorSpeed: -0.3 * Math.PI,
          maxMotorTorque: 5000,
          enableMotor: true,
        });
        this.addRevoluteJoint("cube3", this.dynamicBodies.cube3, ground, this.dynamicBodies.cube3.GetPosition(), {
          motorSpeed: 0.3 * Math.PI,
          maxMotorTorque: 5000,
          enableMotor: true,
        });
        this.addRevoluteJoint("roundBlock", this.dynamicBodies.roundBlock, ground, new b2Vec2(-675 / PHYS_SCALE, -152 / PHYS_SCALE), {
          motorSpeed: -0.9 * Math.PI,
          maxMotorTorque: 5000,
          enableMotor: true,
        });
        break;
      case 11: {
        const killRotateBody = this.requireBody("killRotate", "level 11");
        const trainBody = this.requireBody("train", "level 11");
        const trainWheel1 = this.requireBody("kolesoTrain1", "level 11");
        const trainWheel2 = this.requireBody("kolesoTrain2", "level 11");
        const vagon1 = this.requireBody("vagon1", "level 11");
        this.addRevoluteJoint("killRotate", killRotateBody, ground, killRotateBody.GetPosition(), {
          motorSpeed: -Math.PI,
          maxMotorTorque: 5000,
          enableMotor: true,
        });
        ["killStar0", "killStar1", "killStar2"].forEach((name) => {
          const body = this.getBody(name);
          if (!body) {
            return;
          }
          this.addRevoluteJoint(name, body, ground, new b2Vec2(body.GetPosition().x + 20 / PHYS_SCALE, body.GetPosition().y + 20 / PHYS_SCALE), {
            motorSpeed: -5 * Math.PI,
            maxMotorTorque: 5000,
            enableMotor: true,
          });
        });
        this.addRevoluteJoint("trainWheel1", trainBody, trainWheel1, new b2Vec2(trainWheel1.GetPosition().x + 4 / PHYS_SCALE, trainWheel1.GetPosition().y + 4 / PHYS_SCALE), {
          motorSpeed: 7 * Math.PI,
          maxMotorTorque: 5000,
          enableMotor: true,
        });
        this.addRevoluteJoint("trainWheel2", trainBody, trainWheel2, new b2Vec2(trainWheel2.GetPosition().x + 4 / PHYS_SCALE, trainWheel2.GetPosition().y + 4 / PHYS_SCALE), {
          motorSpeed: 7 * Math.PI,
          maxMotorTorque: 5000,
          enableMotor: true,
        });
        for (let index = 1; index <= 11; index += 1) {
          const wagonBody = this.getBody(`vagon${index}`);
          const wheel1 = this.getBody(`koleso_${index}_1`);
          const wheel2 = this.getBody(`koleso_${index}_2`);
          if (wagonBody && wheel1) {
            this.addRevoluteJoint(`vagon${index}Wheel1`, wagonBody, wheel1, new b2Vec2(wheel1.GetPosition().x + 4 / PHYS_SCALE, wheel1.GetPosition().y + 4 / PHYS_SCALE));
          }
          if (wagonBody && wheel2) {
            this.addRevoluteJoint(`vagon${index}Wheel2`, wagonBody, wheel2, new b2Vec2(wheel2.GetPosition().x + 4 / PHYS_SCALE, wheel2.GetPosition().y + 4 / PHYS_SCALE));
          }
        }
        this.addDistanceJoint("trainLink", trainBody, vagon1, trainBody.GetPosition(), vagon1.GetPosition());
        for (let index = 1; index <= 10; index += 1) {
          const wagonA = this.getBody(`vagon${index}`);
          const wagonB = this.getBody(`vagon${index + 1}`);
          if (!wagonA || !wagonB) {
            continue;
          }
          this.addDistanceJoint(`wagonLink${index}`, wagonA, wagonB, new b2Vec2(wagonA.GetPosition().x - 75 / PHYS_SCALE, wagonA.GetPosition().y), wagonB.GetPosition());
        }
        break;
      }
      case 12:
        this.levelState.killStarDirection = 1;
        this.addPrismaticJoint("killStar", this.dynamicBodies.killStar, ground, new b2Vec2(this.dynamicBodies.killStar.GetPosition().x + 20 / PHYS_SCALE, this.dynamicBodies.killStar.GetPosition().y + 20 / PHYS_SCALE), new b2Vec2(1, 0));
        this.addDistanceJoint("goPlatRoll1", this.dynamicBodies.goPlat, this.dynamicBodies.roll1, new b2Vec2(this.dynamicBodies.goPlat.GetPosition().x + 3, this.dynamicBodies.goPlat.GetPosition().y + 1), new b2Vec2(this.dynamicBodies.roll1.GetPosition().x + 4, this.dynamicBodies.roll1.GetPosition().y + 4));
        this.addDistanceJoint("goPlatRoll2", this.dynamicBodies.goPlat, this.dynamicBodies.roll2, new b2Vec2(this.dynamicBodies.goPlat.GetPosition().x + 57, this.dynamicBodies.goPlat.GetPosition().y + 1), new b2Vec2(this.dynamicBodies.roll2.GetPosition().x + 4, this.dynamicBodies.roll2.GetPosition().y + 4));
        this.addDistanceJoint("rollLink", this.dynamicBodies.roll1, this.dynamicBodies.roll2, new b2Vec2(this.dynamicBodies.roll1.GetPosition().x + 4, this.dynamicBodies.roll1.GetPosition().y + 4), new b2Vec2(this.dynamicBodies.roll2.GetPosition().x + 4, this.dynamicBodies.roll2.GetPosition().y + 4));
        break;
      case 13:
        this.levelState.movePlat1BodyDirection = 1;
        this.levelState.movePlat2BodyDirection = 1;
        this.addPrismaticJoint("movePlat1", this.dynamicBodies.movePlat1, ground, this.dynamicBodies.movePlat1.GetWorldCenter(), new b2Vec2(1, 0));
        this.addPrismaticJoint("movePlat2", this.dynamicBodies.movePlat2, ground, this.dynamicBodies.movePlat2.GetWorldCenter(), new b2Vec2(1, 0));
        this.addDistanceJoint("killStar1", this.dynamicBodies.killStar1, ground, this.dynamicBodies.killStar1.GetPosition(), new b2Vec2(this.dynamicBodies.killStar1.GetPosition().x - 100 / PHYS_SCALE, this.dynamicBodies.killStar1.GetPosition().y - 100 / PHYS_SCALE));
        this.addDistanceJoint("killStar2", this.dynamicBodies.killStar2, ground, this.dynamicBodies.killStar2.GetPosition(), new b2Vec2(this.dynamicBodies.killStar2.GetPosition().x + 100 / PHYS_SCALE, this.dynamicBodies.killStar2.GetPosition().y - 100 / PHYS_SCALE));
        this.dynamicBodies.killStar1.SetAngularVelocity(10);
        this.dynamicBodies.killStar2.SetAngularVelocity(10);
        break;
      case 14:
        this.levelState.movePlatform1BodyDirection = 1;
        this.levelState.killRollGo = false;
        this.addPrismaticJoint("movePlatform1", this.dynamicBodies.movePlatform1, ground, this.dynamicBodies.movePlatform1.GetWorldCenter(), new b2Vec2(0, 1));
        this.addRevoluteJoint("catapult", this.dynamicBodies.catapult, this.dynamicBodies.catapultOpora, this.dynamicBodies.catapult.GetPosition());
        break;
      case 15: {
        this.levelState.movePlatformBodyDirection = 1;
        this.addPrismaticJoint("movePlatform", this.dynamicBodies.movePlatform, ground, this.dynamicBodies.movePlatform.GetWorldCenter(), new b2Vec2(0, 1));
        this.addRevoluteJoint("skateWheel11", this.dynamicBodies.skateBoard1, this.dynamicBodies.skateWheel11, new b2Vec2(this.dynamicBodies.skateWheel11.GetPosition().x + 3 / PHYS_SCALE, this.dynamicBodies.skateWheel11.GetPosition().y + 3 / PHYS_SCALE));
        this.addRevoluteJoint("skateWheel12", this.dynamicBodies.skateBoard1, this.dynamicBodies.skateWheel12, new b2Vec2(this.dynamicBodies.skateWheel12.GetPosition().x + 3 / PHYS_SCALE, this.dynamicBodies.skateWheel12.GetPosition().y + 3 / PHYS_SCALE));
        if (this.checkpointIndex > 0) {
          this.destroyBody(this.dynamicBodies.luk);
        }
        break;
      }
      case 16:
        this.levelState.movePlatform1BodyDirection = 1;
        this.levelState.movePlatform2BodyDirection = 1;
        this.levelState.killRollBall1Direction = 1;
        this.levelState.killRollBall2Direction = -1;
        this.addPrismaticJoint("movePlatform1", this.dynamicBodies.movePlatform1, ground, this.dynamicBodies.movePlatform1.GetWorldCenter(), new b2Vec2(1, 0));
        this.addPrismaticJoint("movePlatform2", this.dynamicBodies.movePlatform2, ground, this.dynamicBodies.movePlatform2.GetWorldCenter(), new b2Vec2(1, 0));
        this.addPrismaticJoint("jumpPlatform1", this.dynamicBodies.jumpPlatform1, ground, this.dynamicBodies.jumpPlatform1.GetWorldCenter(), new b2Vec2(0, 1), {
          lowerTranslation: 0,
          upperTranslation: 1,
          enableLimit: true,
          maxMotorForce: 0,
          motorSpeed: 200,
          enableMotor: true,
        });
        this.addRevoluteJoint("axe1", this.dynamicBodies.axe1, ground, new b2Vec2(2767 / PHYS_SCALE, -60 / PHYS_SCALE));
        break;
      case 17:
        this.levelState.movePlatform1BodyDirection = -1;
        this.levelState.movePlatform2BodyDirection = 1;
        this.addPrismaticJoint("jumpPlatform1", this.dynamicBodies.jumpPlatform1, ground, this.dynamicBodies.jumpPlatform1.GetWorldCenter(), new b2Vec2(0, 1), {
          lowerTranslation: 0,
          upperTranslation: 1,
          enableLimit: true,
          maxMotorForce: 0,
          motorSpeed: 200,
          enableMotor: true,
        });
        this.addPrismaticJoint("movePlatform1", this.dynamicBodies.movePlatform1, ground, this.dynamicBodies.movePlatform1.GetWorldCenter(), new b2Vec2(1, 0));
        this.addPrismaticJoint("movePlatform2", this.dynamicBodies.movePlatform2, ground, this.dynamicBodies.movePlatform2.GetWorldCenter(), new b2Vec2(0, 1));
        this.addRevoluteJoint("star", this.dynamicBodies.star, this.dynamicBodies.kingCrown1, this.dynamicBodies.star.GetPosition());
        break;
      default:
        break;
    }
  }

  startDeath() {
    if (!this.player?.isLive() || this.deathCounter >= 0) {
      return;
    }
    const velocity = this.player.body.GetLinearVelocity();
    this.deathCounter = DEATH_DELAY_FRAMES;
    for (let index = 0; index < 8; index += 1) {
      this.particles.push({
        x: this.player.x - 4 + 5 * Math.random() - 5 * Math.random(),
        y: this.player.y - 4 + 5 * Math.random() - 5 * Math.random(),
        vx: velocity.x / 3 * PHYS_SCALE + 7 * Math.cos(index * 2 * Math.PI / 8),
        vy: velocity.y / 3 * PHYS_SCALE + 7 * Math.sin(index * 2 * Math.PI / 8),
      });
    }
    this.player.kill(this.world);
  }

  startWin() {
    if (this.goalPlaying || this.complete) {
      return;
    }
    this.goalPlaying = true;
    this.winActive = true;
    this.winCounter = this.winDelayFrames;
    this.clock?.Freeze?.(this.levelId === 12 ? 311 : (this.levelId === 17 ? -1 : this.winDelayFrames));
    this.player.body.SetLinearDamping(3);
    this.player.body.SetAngularDamping(3);
  }

  updateAnimations() {
    for (const checkpointState of this.checkpoints) {
      if (checkpointState.playing && checkpointState.currentFrame < CHECKPOINT_FRAME_COUNT) {
        checkpointState.currentFrame += 1;
      } else if (checkpointState.playing) {
        checkpointState.playing = false;
      }
    }
    if (this.goalPlaying && this.goalCurrentFrame < LEVEL_AIM_FRAME_COUNT) {
      this.goalCurrentFrame += 1;
    }
    for (const particle of this.particles) {
      particle.vy += 0.35;
      particle.x += particle.vx * FIXED_TIMESTEP;
      particle.y += particle.vy * FIXED_TIMESTEP;
    }
  }

  handleControls(input) {
    if (this.beginFlag && RIGHT_KEYS.some((code) => input.isDown(code))) {
      this.clock?.unpause?.();
      this.beginFlag = false;
    }
    const position = this.player.body.GetPosition();
    const centerSupport = Boolean(this.getBodyAtPoint(position.x, position.y + 0.4, true));
    const leftSupport = Boolean(this.getBodyAtPoint(position.x - 0.2, position.y + 0.38, true));
    const rightSupport = Boolean(this.getBodyAtPoint(position.x + 0.2, position.y + 0.38, true));
    const hasGroundSupport = centerSupport || leftSupport || rightSupport;
    const velocity = this.player.body.GetLinearVelocity();

    if (LEFT_KEYS.some((code) => input.isDown(code))) {
      this.player.body.SetAwake(true);
      if (hasGroundSupport) {
        if (velocity.x > -5) {
          this.player.body.SetLinearVelocity(new b2Vec2(velocity.x - 0.5, velocity.y));
        }
      } else if (velocity.x > -2.5) {
        this.player.body.SetLinearVelocity(new b2Vec2(velocity.x - 0.25, velocity.y));
      }
    }

    if (RIGHT_KEYS.some((code) => input.isDown(code))) {
      this.player.body.SetAwake(true);
      if (hasGroundSupport) {
        if (velocity.x < 5) {
          this.player.body.SetLinearVelocity(new b2Vec2(velocity.x + 0.5, velocity.y));
        }
      } else if (velocity.x < 2.5) {
        this.player.body.SetLinearVelocity(new b2Vec2(velocity.x + 0.25, velocity.y));
      }
    }

    if (UP_KEYS.some((code) => input.isDown(code))) {
      this.player.body.SetAwake(true);
      if (this.contactListener.playerContactBodies.length > 0 && hasGroundSupport) {
        this.player.body.ApplyForce(new b2Vec2(0, -65), this.player.body.GetWorldCenter());
      } else if (velocity.y < 0) {
        this.player.body.ApplyForce(new b2Vec2(0, -1), this.player.body.GetWorldCenter());
      }
    }
  }

  updateCheckpointsAndGoal() {
    for (const checkpointState of this.checkpoints) {
      if (this.playerOverlapsRect(checkpointState.rect) && checkpointState.currentFrame === 1) {
        this.playCheckpoint(checkpointState.index);
      }
    }
    if (this.playerOverlapsRect(this.goalRect) && this.goalCurrentFrame === 1) {
      this.startWin();
    }
  }

  checkFlashHazards() {
    if (!this.spikesKill || !this.player?.isLive()) {
      return;
    }
    for (const hazard of this.hazardRects) {
      if (this.player.hitTestObjectControlPoints(hazard.rect)) {
        this.startDeath();
        return;
      }
    }
  }

  runLevelScript() {
    switch (this.levelId) {
      case 4: {
        const killBodies = ["killBoom1", "killBoom2", "killBoom3", "axe1", "axe2"];
        if (this.player.y > 650 || killBodies.some((name) => this.bodyIsInPlayerContacts(this.dynamicBodies[name]))) {
          this.startDeath();
        }
        if (this.bodyIsInPlayerContacts(this.dynamicBodies.dropPlatform)) {
          this.destroyBody(this.dynamicBodies.dropPlatform);
          this.recreateBody("dropPlatform", 3);
        }
        ["killBoom1", "killBoom2", "killBoom3"].forEach((name, index) => {
          const stateKey = `${name}Up`;
          const position = this.bodyPixels(name);
          if (position.y > 436) {
            this.levelState[stateKey] = true;
          }
          if (position.y < 327) {
            this.levelState[stateKey] = false;
          }
          if (this.levelState[stateKey]) {
            this.dynamicBodies[name].SetLinearVelocity(new b2Vec2(0, -2));
          }
          void index;
        });
        break;
      }
      case 5:
        if (this.player.y > 650) {
          this.startDeath();
        }
        if (this.hitNamedObject("blueCheck") && this.dynamicBodies.blueBarier) {
          this.destroyBody(this.dynamicBodies.blueBarier);
        }
        if (this.hitNamedObject("greenCheck") && this.dynamicBodies.greenPlatform) {
          this.destroyBody(this.dynamicBodies.greenPlatform);
        }
        break;
      case 6:
        if (this.player.y > 1060) {
          this.startDeath();
        }
        ["drop1", "drop2", "drop3"].forEach((name) => {
          if (this.bodyIsInPlayerContacts(this.dynamicBodies[name])) {
            this.destroyBody(this.dynamicBodies[name]);
            this.recreateBody(name, 3);
          }
        });
        break;
      case 7: {
        if (this.player.y > 500 || this.bodyIsInPlayerContacts(this.dynamicBodies.star)) {
          this.startDeath();
        }
        this.physicsJoints.prismatic.jumpPlatform1.SetMaxMotorForce(this.bodyIsInPlayerContacts(this.dynamicBodies.jumpPlatform1) ? 150 : 0);
        this.physicsJoints.prismatic.jumpPlatform2.SetMaxMotorForce(this.bodyIsInPlayerContacts(this.dynamicBodies.jumpPlatform2) ? 170 : 0);
        const movePlatform1 = this.bodyPixels("movePlatform1");
        if (movePlatform1.y < -30) {
          this.levelState.movePlatform1BodyDirection = 1;
        }
        if (movePlatform1.y > 115) {
          this.levelState.movePlatform1BodyDirection = -1;
        }
        this.dynamicBodies.movePlatform1.SetLinearVelocity(new b2Vec2(0, 3 * this.levelState.movePlatform1BodyDirection));
        const movePlatform2 = this.bodyPixels("movePlatform2");
        if (movePlatform2.x < 1495) {
          this.levelState.movePlatform2BodyDirection = 1;
        }
        if (movePlatform2.x > 1645) {
          this.levelState.movePlatform2BodyDirection = -1;
        }
        this.dynamicBodies.movePlatform2.SetLinearVelocity(new b2Vec2(3 * this.levelState.movePlatform2BodyDirection, 0));
        if (this.hitNamedObject("redCheck") && this.dynamicBodies.redWall) {
          LevelRuntime.levelFlags[7].redCheckLevel = true;
          this.destroyBody(this.dynamicBodies.redWall);
        }
        if (this.hitNamedObject("blueCheck") && this.dynamicBodies.bluePlatform) {
          this.destroyBody(this.dynamicBodies.bluePlatform);
        }
        break;
      }
      case 8: {
        const killers = ["killer1", "killer2", "killer3", "killSpusk", "killSpusk2"];
        if (this.player.y > 1800 || killers.some((name) => this.bodyIsInPlayerContacts(this.dynamicBodies[name]))) {
          this.startDeath();
        }
        ["killer1", "killer2", "killer3"].forEach((name) => {
          const stateKey = `${name}Up`;
          const position = this.bodyPixels(name);
          if (position.y > 70) {
            this.levelState[stateKey] = true;
          }
          if (position.y < -4) {
            this.levelState[stateKey] = false;
          }
          if (this.levelState[stateKey]) {
            this.dynamicBodies[name].SetLinearVelocity(new b2Vec2(0, -4));
          }
        });
        break;
      }
      case 9:
        if (this.player.y > 530 || this.bodyIsInPlayerContacts(this.getBody("killRollBall")) || this.bodyIsInPlayerContacts(this.getBody("boomCrank"))) {
          this.startDeath();
        }
        const greenPlatformBody = this.getBody("greenPlatform");
        if (greenPlatformBody) {
          if (this.bodyPixels("greenPlatform").x < 55) {
            this.levelState.greenPlatformBodyDirection = 1;
          }
          if (this.bodyPixels("greenPlatform").x > 387) {
            this.levelState.greenPlatformBodyDirection = -1;
          }
          greenPlatformBody.SetLinearVelocity(new b2Vec2(2 * this.levelState.greenPlatformBodyDirection, 0));
        }
        ["greenCheck1", "greenCheck2", "greenCheck3"].forEach((name) => {
          if (this.hitNamedObject(name) && this.getBody("greenPlatform")) {
            this.destroyBody(this.getBody("greenPlatform"));
          }
        });
        if (this.bodyPixels("killRollBall").x < 740) {
          this.levelState.killRollBallDirection = 1;
        }
        if (this.bodyPixels("killRollBall").x > 1200) {
          this.levelState.killRollBallDirection = -1;
        }
        this.getBody("killRollBall")?.SetAngularVelocity(5 * this.levelState.killRollBallDirection);
        break;
      case 10:
        if (this.player.y > 500 || this.bodyIsInPlayerContacts(this.dynamicBodies.roundBlock)) {
          this.startDeath();
        }
        ["jumpPlatform1", "jumpPlatform2", "jumpPlatform3"].forEach((name) => {
          this.physicsJoints.prismatic[name].SetMaxMotorForce(this.bodyIsInPlayerContacts(this.dynamicBodies[name]) ? 150 : 0);
        });
        break;
      case 11: {
        const killNames = [
          "killCeil1", "killRotate", "killStar0", "killStar1", "killStar2",
          "killBrevno0", "killBrevno1", "killBrevno2", "killBrevno3",
        ];
        if (this.player.y > 500 || killNames.some((name) => this.bodyIsInPlayerContacts(this.dynamicBodies[name]))) {
          this.startDeath();
        }
        break;
      }
      case 12:
        if (this.player.y > 500 || this.bodyIsInPlayerContacts(this.dynamicBodies.killStar)) {
          this.startDeath();
        }
        if (this.bodyPixels("killStar").x < -1050) {
          this.levelState.killStarDirection = 1;
        }
        if (this.bodyPixels("killStar").x > -824) {
          this.levelState.killStarDirection = -1;
        }
        this.dynamicBodies.killStar.SetLinearVelocity(new b2Vec2(4 * this.levelState.killStarDirection, 0));
        break;
      case 13:
        if (this.player.y > 500 || ["kingStarBody1", "killStar1", "killStar2"].some((name) => this.dynamicBodies[name] && this.bodyIsInPlayerContacts(this.dynamicBodies[name]))) {
          this.startDeath();
        }
        if (this.bodyPixels("movePlat1").x < -1237) {
          this.levelState.movePlat1BodyDirection = 1;
        }
        if (this.bodyPixels("movePlat1").x > -863) {
          this.levelState.movePlat1BodyDirection = -1;
        }
        this.dynamicBodies.movePlat1.SetLinearVelocity(new b2Vec2(3 * this.levelState.movePlat1BodyDirection, 0));
        if (this.bodyPixels("movePlat2").x < -572) {
          this.levelState.movePlat2BodyDirection = 1;
        }
        if (this.bodyPixels("movePlat2").x > -68) {
          this.levelState.movePlat2BodyDirection = -1;
        }
        this.dynamicBodies.movePlat2.SetLinearVelocity(new b2Vec2(3 * this.levelState.movePlat2BodyDirection, 0));
        if (this.hitNamedObject("greenCheck") && this.dynamicBodies.greenBarier) {
          LevelRuntime.levelFlags[13].greenCheckLevel = true;
          this.destroyBody(this.dynamicBodies.greenBarier);
        }
        break;
      case 14:
        if (this.player.y > 750 || this.bodyIsInPlayerContacts(this.dynamicBodies.killRollBall)) {
          this.startDeath();
        }
        if (this.bodyPixels("movePlatform1").y < 232) {
          this.levelState.movePlatform1BodyDirection = 1;
        }
        if (this.bodyPixels("movePlatform1").y > 640) {
          this.levelState.movePlatform1BodyDirection = -1;
        }
        this.dynamicBodies.movePlatform1.SetLinearVelocity(new b2Vec2(0, 3 * this.levelState.movePlatform1BodyDirection));
        if (this.hitNamedObject("blueCheck") && this.dynamicBodies.bluePlat) {
          this.destroyBody(this.dynamicBodies.bluePlat);
        }
        if (this.bodyIsInPlayerContacts(this.dynamicBodies.dropPlat)) {
          this.destroyBody(this.dynamicBodies.dropPlat);
          this.recreateBody("dropPlat", 1);
        }
        if (this.player.x > 1410 && !this.levelState.killRollGo) {
          this.levelState.killRollGo = true;
          this.dynamicBodies.killRollBall.ApplyImpulse(new b2Vec2(-5, 0), this.dynamicBodies.killRollBall.GetPosition());
        }
        break;
      case 15:
        if (this.player.y > 860 || this.hitNamedObject("killLine")) {
          this.startDeath();
        }
        if (this.hitNamedObject("redCheck") && this.dynamicBodies.redGate) {
          this.destroyBody(this.dynamicBodies.redGate);
          const redGateBody = this.recreateBody("redGate", 1);
          this.addRevoluteJoint("redGateHinge", this.dynamicBodies.shopLeftSide, redGateBody, redGateBody.GetPosition());
          redGateBody.SetAngularVelocity(3);
        }
        if (this.bodyPixels("movePlatform").y < 211) {
          this.levelState.movePlatformBodyDirection = 1;
        }
        if (this.bodyPixels("movePlatform").y > 368) {
          this.levelState.movePlatformBodyDirection = -1;
        }
        this.dynamicBodies.movePlatform.SetLinearVelocity(new b2Vec2(0, 3 * this.levelState.movePlatformBodyDirection));
        if (this.levelData.layout.namedChildren.killLine) {
          this.levelData.layout.namedChildren.killLine.x = this.levelData.layout.namedChildren.killLine.x < 73
            ? 387
            : this.levelData.layout.namedChildren.killLine.x - 4;
        }
        break;
      case 16:
        if (this.player.y > 1000 || ["killRollBall1", "killRollBall2", "axe1"].some((name) => this.bodyIsInPlayerContacts(this.dynamicBodies[name]))) {
          this.startDeath();
        }
        if (this.hitNamedObject("wrongWay")) {
          this.checkpointIndex = 0;
          LevelRuntime.levelFlags[16].isStrelka = true;
        }
        if (this.bodyPixels("movePlatform1").x < 457) {
          this.levelState.movePlatform1BodyDirection = 1;
        }
        if (this.bodyPixels("movePlatform1").x > 787) {
          this.levelState.movePlatform1BodyDirection = -1;
        }
        this.dynamicBodies.movePlatform1.SetLinearVelocity(new b2Vec2(3 * this.levelState.movePlatform1BodyDirection, 0));
        if (this.bodyPixels("movePlatform2").x < 999) {
          this.levelState.movePlatform2BodyDirection = 1;
        }
        if (this.bodyPixels("movePlatform2").x > 1350) {
          this.levelState.movePlatform2BodyDirection = -1;
        }
        this.dynamicBodies.movePlatform2.SetLinearVelocity(new b2Vec2(3 * this.levelState.movePlatform2BodyDirection, 0));
        this.physicsJoints.prismatic.jumpPlatform1.SetMaxMotorForce(this.bodyIsInPlayerContacts(this.dynamicBodies.jumpPlatform1) ? 250 : 0);
        ["blueCheck1", "blueCheck2", "blueCheck3"].forEach((name) => {
          if (this.hitNamedObject(name) && this.dynamicBodies.bluePlato) {
            this.destroyBody(this.dynamicBodies.bluePlato);
          }
        });
        if (this.bodyIsInPlayerContacts(this.dynamicBodies.dropPlatform)) {
          this.destroyBody(this.dynamicBodies.dropPlatform);
          this.recreateBody("dropPlatform", 3);
        }
        if (this.bodyPixels("killRollBall1").x < 1450) {
          this.levelState.killRollBall1Direction = 1;
        }
        if (this.bodyPixels("killRollBall1").x > 1750) {
          this.levelState.killRollBall1Direction = -1;
        }
        this.dynamicBodies.killRollBall1.SetAngularVelocity(5 * this.levelState.killRollBall1Direction);
        if (this.bodyPixels("killRollBall2").x < 2120) {
          this.levelState.killRollBall2Direction = 1;
        }
        if (this.bodyPixels("killRollBall2").x > 2495) {
          this.levelState.killRollBall2Direction = -1;
        }
        this.dynamicBodies.killRollBall2.SetAngularVelocity(5 * this.levelState.killRollBall2Direction);
        break;
      case 17:
        if (this.player.y > 960 || this.bodyIsInPlayerContacts(this.dynamicBodies.killStarPart)) {
          this.startDeath();
        }
        this.physicsJoints.prismatic.jumpPlatform1.SetMaxMotorForce(this.bodyIsInPlayerContacts(this.dynamicBodies.jumpPlatform1) ? 150 : 0);
        this.dynamicBodies.star.SetAngularVelocity(-1);
        if (this.bodyPixels("movePlatform1").x < 903) {
          this.levelState.movePlatform1BodyDirection = 1;
        }
        if (this.bodyPixels("movePlatform1").x > 1123) {
          this.levelState.movePlatform1BodyDirection = -1;
        }
        this.dynamicBodies.movePlatform1.SetLinearVelocity(new b2Vec2(3 * this.levelState.movePlatform1BodyDirection, 0));
        if (this.bodyPixels("movePlatform2").y < 272) {
          this.levelState.movePlatform2BodyDirection = 1;
        }
        if (this.bodyPixels("movePlatform2").y > 430) {
          this.levelState.movePlatform2BodyDirection = -1;
        }
        this.dynamicBodies.movePlatform2.SetLinearVelocity(new b2Vec2(0, 3 * this.levelState.movePlatform2BodyDirection));
        break;
      default:
        if (this.player.y > this.config.deathY) {
          this.startDeath();
        }
        break;
    }
  }

  updateCamera() {
    const targetCameraX = -this.player.x + 275;
    const targetCameraY = -this.player.y + 200;
    this.camera.x += (targetCameraX - this.camera.x) * CAMERA_EASE;
    this.camera.y += (targetCameraY - this.camera.y) * CAMERA_EASE;
  }

  update(input) {
    this.updateAnimations();

    if (this.deathCounter >= 0) {
      this.world.Step(FIXED_TIMESTEP, VELOCITY_ITERATIONS, POSITION_ITERATIONS);
      this.world.ClearForces();
      this.refreshNamedRects();
      this.runLevelScript();
      this.refreshNamedRects();
      this.clock?.update();
      this.deathCounter -= 1;
      if (this.deathCounter <= 0) {
        this.failed = true;
      }
      return;
    }

    if (this.winCounter >= 0) {
      this.world.Step(FIXED_TIMESTEP, VELOCITY_ITERATIONS, POSITION_ITERATIONS);
      this.world.ClearForces();
      this.refreshNamedRects();
      this.runLevelScript();
      this.refreshNamedRects();
      this.clock?.update();
      this.updateCamera();
      this.winCounter -= 1;
      if (this.winCounter <= 0) {
        this.winActive = false;
        this.complete = true;
      }
      return;
    }

    this.world.Step(FIXED_TIMESTEP, VELOCITY_ITERATIONS, POSITION_ITERATIONS);
    this.world.ClearForces();
    this.refreshNamedRects();
    this.handleControls(input);
    this.updateCheckpointsAndGoal();
    this.checkFlashHazards();
    this.runLevelScript();
    this.refreshNamedRects();
    this.clock?.update();
    this.updateCamera();
  }

  renderCheckpoints(ctx) {
    if (!this.assetsEnabled) {
      return;
    }
    for (const checkpointState of this.checkpoints) {
      const frame = this.checkpointFrames[checkpointState.currentFrame - 1];
      if (frame?.complete) {
        drawAnimatedFrame(ctx, frame, { layout: { imageOffsetX: 0, imageOffsetY: 0, imageWidth: checkpointState.rect.width, imageHeight: checkpointState.rect.height } }, checkpointState.rect.x, checkpointState.rect.y);
      }
    }
  }

  renderGoal(ctx) {
    if (!this.assetsEnabled) {
      return;
    }
    const frame = this.goalFrames[this.goalCurrentFrame - 1];
    if (frame?.complete && this.goalSymbol) {
      drawAnimatedFrame(ctx, frame, this.goalSymbol, this.goalPosition.x, this.goalPosition.y);
    }
  }

  render(ctx, stageWidth, stageHeight, options = {}) {
    const zoom = Math.min(4, Math.max(0.1, Number(options.zoom) || 1));
    ctx.save();
    ctx.clearRect(0, 0, stageWidth, stageHeight);
    ctx.translate(stageWidth / 2, stageHeight / 2);
    ctx.scale(zoom, zoom);
    ctx.translate(-stageWidth / 2, -stageHeight / 2);
    ctx.translate(this.camera.x, this.camera.y);

    if (this.image?.complete) {
      drawStageImage(ctx, this.image, this.levelData);
    }

    this.renderCheckpoints(ctx);
    this.renderGoal(ctx);

    ctx.fillStyle = "#d32f2f";
    if (this.player?.alive) {
      ctx.beginPath();
      ctx.arc(this.player.x, this.player.y, this.player.radius, 0, Math.PI * 2);
      ctx.fill();
    }

    ctx.restore();
  }
}
